import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { DropDownAsync } from "@/components/forms/inputs/dropDownAsync";
import { api } from "@/helper/api";
import { useDispatch, useSelector } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import { RootState } from "@/components/state/store";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import { useTranslation } from "react-i18next";
import styles from "@/components/forms/inputs/dropDownSubjectSearch.module.css";
import InfoIcon from "@mui/icons-material/Info";
import { GroupBase, StylesConfig } from "react-select";
import { MandatoryMarker } from "@/components/forms/inputs/mandatoryMarker";
import DraftBadge from "@/components/draftBadge/draftBadge";
import type {
  QuickSearchSelectionEnrichmentConfig,
  QuickSearchSelectionMetadata,
  QuickSearchSelectionValidationState,
} from "@/types/QuickSearch";
import {
  isCompanySubjectSelection,
  normalizeQuickSearchResponse,
  toCompanyOption,
} from "@/helper/quickSearchSelection";

const isDraftSubjectSearchValue = (value: any) => value?.is_draft === true;

interface Props {
  subdomain?: any;
  options?: any;
  wrapperStyle?: any;
  wrapperClassName?: string;
  onChange?: any;
  preExistingValue?: any;
  disabled?: any;
  name?: any;
  error?: any;
  customRef?: any;
  resetInput?: any;
  isClearable?: boolean;
  queryParameter?: "users" | "companies";
  companyIdforParent?: string;
  autofocus?: boolean;
  useGlobalState?: boolean;
  placeholder?: React.ReactNode;
  companyId?: any;
  isMulti?: boolean;
  additionalSearchParameters?: any;
  companyIdForFiltering?: any; //for providing company Id & filtering users for the company
  subjectIdForFiltering?: any; //for providing company subject id & filtering users for the company
  mandatory?: boolean;
  allowSubjectSearchForNonAdmin?: boolean;
  includeDeletedSubjects?: boolean;
  customStyles?: StylesConfig<any, any, GroupBase<any>> | undefined;
  alignHeight?: boolean;
  selectionEnrichment?: QuickSearchSelectionEnrichmentConfig;
  getSelectionBlockedMessage?: (
    selection: QuickSearchSelectionMetadata,
  ) => ReactNode;
  onSelectionValidationChange?: (
    state: QuickSearchSelectionValidationState,
  ) => void;
}

export const DropDownSubjectSearch: FC<Props> = ({
  subdomain,
  options,
  wrapperStyle,
  wrapperClassName,
  onChange,
  preExistingValue,
  disabled,
  name,
  error,
  customRef,
  resetInput,
  isClearable,
  queryParameter,
  companyIdforParent,
  autofocus,
  useGlobalState,
  placeholder,
  companyId,
  isMulti,
  additionalSearchParameters,
  companyIdForFiltering,
  subjectIdForFiltering,
  mandatory,
  allowSubjectSearchForNonAdmin = false,
  includeDeletedSubjects = false,
  alignHeight = false,
  selectionEnrichment,
  getSelectionBlockedMessage,
  onSelectionValidationChange,
}) => {
  const { t } = useTranslation();
  const [localPreExistingValue, setLocalPreExistingValue] =
    useState<any>(preExistingValue);
  const { handleError: handleApiError, errorModal } = useErrorModal();
  const [deletedSelected, setDeletedSelected] = useState<boolean>(false);
  const [draftSelected, setDraftSelected] = useState<boolean>(false);
  const [blockedSelection, setBlockedSelection] =
    useState<QuickSearchSelectionMetadata | null>(null);
  const validatedGlobalSelectionRef = useRef<string | null>(null);

  const dispatch = useDispatch();
  const persistedSubjectSearchValue = useSelector(
    (state: RootState) => state.subjectSearch,
  );

  const onChangeHandler = (selectedOption: any) => {
    if (
      selectionEnrichment &&
      selectedOption &&
      selectedOption.selection?.selectable !== true
    ) {
      return;
    }

    if (!selectedOption) {
      setDeletedSelected(false);
      setDraftSelected(false);
      setBlockedSelection(null);
      validatedGlobalSelectionRef.current = null;
      onSelectionValidationChange?.("idle");
    } else if (selectionEnrichment) {
      setBlockedSelection(null);
      validatedGlobalSelectionRef.current = `${selectionEnrichment.responseField}:${selectionEnrichment.booleanField}:${selectedOption.value}`;
      onSelectionValidationChange?.("valid");
    }
    if (useGlobalState) {
      if (
        selectedOption &&
        typeof selectedOption === "object" &&
        selectedOption.value !== persistedSubjectSearchValue.value
      ) {
        const isDeletedUser = selectedOption.is_deleted === true;
        const isInactiveCompany = selectedOption.is_active === false;
        setDraftSelected(isDraftSubjectSearchValue(selectedOption));

        if (isDeletedUser || isInactiveCompany) {
          if (typeof onChange === "function") {
            onChange(selectedOption);
          }
          setDeletedSelected(true);
          return;
        }

        setDeletedSelected(false);
        dispatch(setSubjectSearch(selectedOption));
      }
    } else {
      if (selectedOption && typeof selectedOption === "object") {
        const isDeletedUser = selectedOption.is_deleted === true;
        const isInactiveCompany = selectedOption.is_active === false;
        setDeletedSelected(isDeletedUser || isInactiveCompany);
        setDraftSelected(isDraftSubjectSearchValue(selectedOption));
      } else if (Array.isArray(selectedOption) && selectedOption.length !== 0) {
        const hasDeletedOrInactive = selectedOption.some(
          (opt) => opt.is_deleted === true || opt.is_active === false,
        );

        setDeletedSelected(hasDeletedOrInactive);
        setDraftSelected(selectedOption.some(isDraftSubjectSearchValue));
        onChange(selectedOption);
        return;
      } else {
        setDeletedSelected(false);
        setDraftSelected(false);
      }
      onChange?.(selectedOption);
    }
  };

  useEffect(() => {
    const hasPersistedSubject = Boolean(
      persistedSubjectSearchValue &&
        Object.keys(persistedSubjectSearchValue).length !== 0,
    );
    const isCompany = isCompanySubjectSelection(
      persistedSubjectSearchValue,
    );

    const shouldExecute =
      (queryParameter === "companies" && isCompany) ||
      queryParameter === undefined ||
      (queryParameter === "users" && !isCompany);

    if (
      useGlobalState &&
      hasPersistedSubject &&
      shouldExecute &&
      !(selectionEnrichment && isCompany)
    ) {
      setLocalPreExistingValue(persistedSubjectSearchValue);
      onChange?.(persistedSubjectSearchValue);

      const isDeletedUser =
        (persistedSubjectSearchValue as any)?.is_deleted === true;
      const isInactiveCompany =
        (persistedSubjectSearchValue as any)?.is_active === false;
      setDeletedSelected(isDeletedUser || isInactiveCompany);
      setDraftSelected(isDraftSubjectSearchValue(persistedSubjectSearchValue));
    }

    if (preExistingValue !== undefined && preExistingValue !== null) {
      if (Array.isArray(preExistingValue) && preExistingValue.length !== 0) {
        const [firstValue] = preExistingValue;
        if (
          firstValue &&
          firstValue.value !== localPreExistingValue?.value &&
          firstValue.label !== localPreExistingValue?.label
        ) {
          setLocalPreExistingValue(preExistingValue);
          onChange?.(preExistingValue);
          const isDeletedUser = firstValue?.is_deleted === true;
          const isInactiveCompany = firstValue?.is_active === false;
          setDeletedSelected(isDeletedUser || isInactiveCompany);
          setDraftSelected(preExistingValue.some(isDraftSubjectSearchValue));
        }
      } else if (
        preExistingValue.value !== localPreExistingValue?.value &&
        preExistingValue.label !== localPreExistingValue?.label
      ) {
        setLocalPreExistingValue(preExistingValue);
        onChange?.(preExistingValue);
        const isDeletedUser = preExistingValue?.is_deleted === true;
        const isInactiveCompany = preExistingValue?.is_active === false;
        setDeletedSelected(isDeletedUser || isInactiveCompany);
        setDraftSelected(isDraftSubjectSearchValue(preExistingValue));
      }
    }
  }, [
    persistedSubjectSearchValue,
    preExistingValue,
    queryParameter,
    selectionEnrichment,
    useGlobalState,
  ]);

  useEffect(() => {
    const hasPersistedCompany = Boolean(
      selectionEnrichment &&
        useGlobalState &&
        queryParameter === "companies" &&
        persistedSubjectSearchValue &&
        Object.keys(persistedSubjectSearchValue).length !== 0 &&
        isCompanySubjectSelection(persistedSubjectSearchValue),
    );
    if (!hasPersistedCompany || !selectionEnrichment) {
      validatedGlobalSelectionRef.current = null;
      return;
    }

    const validationKey = `${selectionEnrichment.responseField}:${selectionEnrichment.booleanField}:${persistedSubjectSearchValue.value}`;
    if (validatedGlobalSelectionRef.current === validationKey) {
      return;
    }

    validatedGlobalSelectionRef.current = validationKey;
    setLocalPreExistingValue(persistedSubjectSearchValue);
    onSelectionValidationChange?.("validating");
    api.genericApiRequest({
      method: "get",
      entity: "subject/quicksearch",
      parametersToRender: {
        additionalUrlParameters: {
          show: "companies",
          company_subject__in: String(persistedSubjectSearchValue.value),
          enrich_response_with: selectionEnrichment.responseField,
        },
        depth: "0",
      },
      successHandler: (res: any) => {
        const normalizedData = normalizeQuickSearchResponse(
          res.data,
          selectionEnrichment,
        ) as any;
        const company = (normalizedData?.companies ?? []).find(
          (result: any) =>
            String(result.subject_id) ===
            String(persistedSubjectSearchValue.value),
        );
        const selection = company?.selection as
          | QuickSearchSelectionMetadata
          | undefined;

        if (company && selection?.selectable === true) {
          const companyOption = toCompanyOption(company);
          setLocalPreExistingValue(companyOption);
          setBlockedSelection(null);
          setDeletedSelected(false);
          setDraftSelected(isDraftSubjectSearchValue(companyOption));
          dispatch(setSubjectSearch(companyOption));
          onChange?.(companyOption);
          onSelectionValidationChange?.("valid");
          return;
        }

        setLocalPreExistingValue(null);
        setDeletedSelected(false);
        setDraftSelected(false);
        setBlockedSelection(selection ?? null);
        dispatch({ type: "clearSubjectSearch" });
        onChange?.(null);
        onSelectionValidationChange?.("invalid");
      },
      failHandler: (requestError: any) => {
        handleApiError(requestError);
        onSelectionValidationChange?.("invalid");
      },
    });
  }, [
    dispatch,
    handleApiError,
    onChange,
    onSelectionValidationChange,
    persistedSubjectSearchValue,
    queryParameter,
    selectionEnrichment,
    useGlobalState,
  ]);

  const resolvedPlaceholder = placeholder ?? t("search_placeholder");

  return (
    <>
      <DropDownAsync
        dataCallback={(inputValue: string, finishCallback: any) => {
          const additionalUrlParameters: any = {};

          additionalUrlParameters["search"] = inputValue;
          if (queryParameter) {
            additionalUrlParameters["show"] = queryParameter;
          }
          if (includeDeletedSubjects) {
            additionalUrlParameters["include_deleted"] = "true";
          }
          if (additionalSearchParameters) {
            for (const key in additionalSearchParameters) {
              additionalUrlParameters[key] = additionalSearchParameters[key];
            }
          }
          if (selectionEnrichment) {
            additionalUrlParameters["enrich_response_with"] =
              selectionEnrichment.responseField;
          }
          if (subdomain === "admin") {
            if (companyIdForFiltering !== undefined) {
              if (Array.isArray(companyIdForFiltering)) {
                additionalUrlParameters["company_id__in"] =
                  companyIdForFiltering;
              } else
                additionalUrlParameters["company_id"] = companyIdForFiltering;
            } else if (subjectIdForFiltering !== undefined) {
              if (Array.isArray(subjectIdForFiltering)) {
                additionalUrlParameters["company_subject__in"] =
                  subjectIdForFiltering;
              } else
                additionalUrlParameters["company_subject"] =
                  subjectIdForFiltering;
            }
          }
          const isSearchAllowed =
            subdomain === "admin" || allowSubjectSearchForNonAdmin;
          if (isSearchAllowed && inputValue.length > 2) {
            api.genericApiRequest({
              method: "get",
              entity: "subject/quicksearch",
              parametersToRender: {
                additionalUrlParameters: additionalUrlParameters,
                depth: "0",
              },
              successHandler: (res: any) => {
                const normalizedData = selectionEnrichment
                  ? normalizeQuickSearchResponse(
                      res.data,
                      selectionEnrichment,
                    )
                  : res.data;
                const subjects = [];
                let users;
                const companies = (normalizedData as any)["companies"] ?? [];

                if (companyId && companyId !== undefined) {
                  users = ((normalizedData as any)["users"] ?? []).filter(
                    (x: any) => x.company_id === companyId,
                  );
                } else users = (normalizedData as any)["users"] ?? [];

                const userArray = [];
                const companyArray = [];
                for (const key in users) {
                  const label = users[key].name;
                  const eMail = users[key].email;
                  const value = users[key].subject_id;
                  const companyName = users[key].company_name;
                  const companyId = users[key].company_id;
                  const isCompany = false;

                  let userLabel = label;
                  if (eMail !== "") {
                    userLabel = label + " " + `(${eMail} - ${companyName}) `;
                  }
                  userArray.push({
                    value: value,
                    label: userLabel,
                    is_deleted: users[key].is_deleted,
                    is_company: isCompany,
                    company_id: companyId,
                    ...(users[key].selection
                      ? {
                          selection: users[key].selection as QuickSearchSelectionMetadata,
                        }
                      : {}),
                  });
                }
                for (const key in companies) {
                  companyArray.push(toCompanyOption(companies[key]));
                }

                if (companyIdforParent && companyIdforParent !== "") {
                  const filtered = companyArray.filter((company: any) => {
                    return company.companyId !== companyIdforParent;
                  });
                  subjects.push(...filtered, ...userArray);
                } else subjects.push(...companyArray, ...userArray);

                finishCallback(subjects);
              },
              failHandler: (error: any) => {
                handleApiError(error);
              },
            });
          } else {
            finishCallback([]);
          }
        }}
        wrapperStyle={wrapperStyle}
        wrapperClassName={wrapperClassName}
        options={options}
        onChange={onChangeHandler}
        isSearchable={true}
        preExistingValue={localPreExistingValue}
        disabled={disabled}
        name={name}
        error={error}
        ref={customRef}
        isClearable={isClearable}
        resetInput={resetInput}
        autofocus={autofocus}
        placeholder={
          mandatory ? (
            <>
              {resolvedPlaceholder}
              <MandatoryMarker />
            </>
          ) : (
            resolvedPlaceholder
          )
        }
        isMulti={isMulti}
        useGlobalstate={useGlobalState}
        alignHeight={alignHeight}
        isOptionDisabled={(option: any) =>
          Boolean(selectionEnrichment) &&
          option.selection?.selectable !== true
        }
        formatOptionLabel={(option: any) => {
          const classes = [] as string[];
          if (option.isParent) classes.push(styles.boldText);
          if (option.is_company && !option.hasParent && !option.isParent)
            classes.push(styles.boldText);
          if (option.is_deleted === true) classes.push(styles.deletedUser);
          if (option.is_active === false) classes.push(styles.inactiveCompany);
          const isSelectionBlocked =
            option.selection?.selectable === false;
          if (isSelectionBlocked) {
            classes.push(styles.selectionBlockedLabel);
          }
          const blockedMessage =
            isSelectionBlocked && getSelectionBlockedMessage
              ? getSelectionBlockedMessage(option.selection)
              : null;
          const isDraft = isDraftSubjectSearchValue(option);

          if (classes.length > 0 || blockedMessage || isDraft) {
            return (
              <span className={styles.optionContent}>
                <span className={styles.optionLabelRow}>
                  <span className={classes.join(" ")}>{option.label}</span>
                  {isDraft && <DraftBadge />}
                </span>
                {blockedMessage && (
                  <span className={styles.selectionBlockedReason}>
                    {blockedMessage}
                  </span>
                )}
              </span>
            );
          }
          return option.label;
        }}
      />
      {deletedSelected && (
        <span className={styles.deletedSubjectSelected} role="status">
          <InfoIcon fontSize="small" className={styles.infoIcon} />
          {t("deleted_subject_selected")}
        </span>
      )}
      {draftSelected && (
        <span className={styles.deletedSubjectSelected} role="status">
          <InfoIcon fontSize="small" className={styles.infoIcon} />
          {t("draft_subject_selected")}
        </span>
      )}
      {blockedSelection && getSelectionBlockedMessage && (
        <span className={styles.deletedSubjectSelected} role="status">
          <InfoIcon fontSize="small" className={styles.infoIcon} />
          {getSelectionBlockedMessage(blockedSelection)}
        </span>
      )}
      {errorModal}
    </>
  );
};

export default DropDownSubjectSearch;
